import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CircleAlert, Clock } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { timeAgo } from '@/utils/timeUtils'
import { useTranslation } from '@/i18n'
import { listWorkflowRuns } from './workflowApi'
import { buildWorkflowNotifications } from './workflowNotifications'
import type { Workflow } from './types'
import type { WorkflowRunView } from './workflowTypes'

/**
 * Library notification bell (Figma "8. Edge Cases" — Alert): red-dotted when a
 * run is parked on the user; the dropdown lists "Needs your review" (parked
 * runs) and "Failed runs", each row opening that workflow's run in the builder.
 */
export default function WorkflowNotificationsBell({ workflows }: { workflows: Workflow[] }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [runs, setRuns] = useState<WorkflowRunView[]>([])

  // The runs API filters by a SINGLE status, so the bell fetches a wide recent
  // window (200, not 50 — a parked run must not fall off the horizon behind a
  // burst of newer runs) and classifies client-side. Re-polled every 30s so a
  // run that parks while the page is open shows up without a reload.
  useEffect(() => {
    let active = true
    const load = () =>
      listWorkflowRuns({ limit: 200 })
        .then(({ runs: all }) => {
          if (active) setRuns(all ?? [])
        })
        .catch(() => {
          // Keep the last known entries on a transient fetch error.
        })
    void load()
    const interval = setInterval(() => void load(), 30_000)
    return () => {
      active = false
      clearInterval(interval)
    }
  }, [])

  const nameById = useMemo(
    () => Object.fromEntries(workflows.map((w) => [w.id, w.name])),
    [workflows]
  )
  const notifications = useMemo(
    () => buildWorkflowNotifications(runs, nameById),
    [runs, nameById]
  )
  const hasAny =
    notifications.needsReview.length > 0 || notifications.failedRuns.length > 0

  const open = (templateId: string, runId: string) =>
    navigate(
      `/workflow/new?templateId=${encodeURIComponent(templateId)}&runId=${encodeURIComponent(runId)}`
    )

  const row = (
    entry: { runId: string; templateId: string; title: string; subtitle: string; at: string | null },
    tone: 'review' | 'failed'
  ) => (
    <button
      key={entry.runId}
      type="button"
      onClick={() => open(entry.templateId, entry.runId)}
      className="flex w-full min-w-0 items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/60"
    >
      <span
        className={
          tone === 'review'
            ? 'mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-amber-300 text-amber-500 dark:border-amber-700'
            : 'mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-red-300 text-red-500 dark:border-red-800'
        }
      >
        {tone === 'review' ? <Clock className="size-4" /> : <CircleAlert className="size-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{entry.title}</span>
        <span className="block truncate text-xs text-muted-foreground">{entry.subtitle}</span>
      </span>
      {entry.at && (
        <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(entry.at)}</span>
      )}
    </button>
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex size-5 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={t('workflow:notifications', { defaultValue: 'Notifications' })}
        >
          <Bell className="size-4" />
          {notifications.needsReview.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-red-500" aria-hidden />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[420px] max-w-[90vw] p-0"
        aria-label={t('workflow:notifications', { defaultValue: 'Notifications' })}
      >
        {!hasAny ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('workflow:bell.empty', { defaultValue: 'Nothing needs your attention' })}
          </p>
        ) : (
          <div className="max-h-[400px] overflow-y-auto py-2">
            {notifications.needsReview.length > 0 && (
              <>
                <p className="px-4 pb-1 pt-2 text-sm text-muted-foreground">
                  {t('workflow:bell.needs_review', { defaultValue: 'Needs your review' })}
                </p>
                {notifications.needsReview.map((entry) => row(entry, 'review'))}
              </>
            )}
            {notifications.failedRuns.length > 0 && (
              <>
                <p className="px-4 pb-1 pt-2 text-sm text-muted-foreground">
                  {t('workflow:bell.failed_runs', { defaultValue: 'Failed runs' })}
                </p>
                {notifications.failedRuns.map((entry) => row(entry, 'failed'))}
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
